import type { FC, ReactNode } from "react";
import { IconCheck, IconAlertCircleFilled } from "@web-monorepo/dds-icons";
import styles from "./DDSToast.module.css";
import { DDSButton } from "../../Buttons/DDSButton/DDSButton";
import { TranslatedString } from "@web-monorepo/translation-runtime";

export const Variants = ["neutral", "success", "error", "warning"] as const;
export type Variant = (typeof Variants)[keyof typeof Variants];

type IconType = FC<{ className: string }>;

type PropsWithIcon = {
  children?: never;
  variant?: "neutral";
  icon?: IconType;
  /** @deprecated Use children prop instead for more flexible content rendering */
  text: TranslatedString;
  /** @deprecated Use children prop with DDSButton component instead */
  onClickAction?: () => void;
  /** @deprecated Use children prop with DDSButton component instead */
  actionName?: TranslatedString;
};

type PropsWithoutIcon = {
  children?: never;
  variant: Variant;
  /** @deprecated Use children prop instead for more flexible content rendering */
  text: TranslatedString;
  icon?: never;
  /** @deprecated Use children prop with DDSButton component instead */
  onClickAction?: () => void;
  /** @deprecated Use children prop with DDSButton component instead */
  actionName?: TranslatedString;
};

type PropsWithChildren = {
  children: ReactNode;
  text?: never;
  variant: Variant;

  icon?: IconType | null;
  onClickAction?: never;
  actionName?: never;
};

const NoIcon: IconType = () => null;

const iconForVariant = (variant: Variant): IconType => {
  switch (variant) {
    case "success":
      return IconCheck;
    case "error":
    case "warning":
      return IconAlertCircleFilled;
  }

  return NoIcon;
};

export type Props = PropsWithIcon | PropsWithoutIcon | PropsWithChildren;

export const DDSToast: FC<Props> = ({
  children,
  variant = "neutral",
  icon: ProvidedIcon,
  text,
  onClickAction,
  actionName,
}) => {
  if (actionName && !onClickAction) {
    throw new Error("DDSToast: onClickAction is required when actionName is provided.");
  }

  const Icon = ProvidedIcon === null ? null : (ProvidedIcon ?? iconForVariant(variant));

  return (
    <aside className={[styles.toast, styles[variant as keyof typeof styles]].join(" ")}>
      {Icon ? <Icon className={styles.icon} /> : null}
      {children ? children : <p className={styles.text}>{text}</p>}
      {actionName && (
        <DDSButton
          data-name="toast-action-button"
          variant="unstyled"
          size="small"
          className={styles.actionButton}
          onClick={onClickAction}
        >
          {actionName}
        </DDSButton>
      )}
    </aside>
  );
};
